<template>
  <div v-if="authStore.isAuthInitialized && !authStore.isSiteAuthenticated" class="auth-background">
    <div class="auth-overlay">
      <div v-if="! showForm">
        <div class="auth-header auth-header--maintenance">
          <h2>We're launching something new on A Sound Effect – and we can't wait to share it with you!</h2>
          <p>
            The site is temporarily unavailable while we get everything in place for you, but we'll be back soon.
            </br>If you need support during this period, please email <a href="mailto:orders@asoundeffect.com">orders@asoundeffect.com</a>
          </p>
        </div>
        <section class="auth_social-links">
          <a class="social-links__link" href="https://www.facebook.com/asoundeffects/" target="_blank" title="Facebook">
            <span class="icon"><img src="https://www.asoundeffect.com/wp-content/maintenance/assets/images/facebook.svg" alt="Facebook"></span>
          </a>
          <a class="social-links__link" href="https://x.com/asoundeffect" target="_blank" title="Twitter">
            <span class="icon"><img src="https://www.asoundeffect.com/wp-content/maintenance/assets/images/twitter.svg" alt="Twitter"></span>
          </a>
          <a class="social-links__link" href="https://www.instagram.com/asoundeffect_/" target="_blank" title="Instagram">
            <span class="icon"><img src="https://www.asoundeffect.com/wp-content/maintenance/assets/images/instagram.svg" alt="Instagram"></span>
          </a>
        </section>
      </div>
      <div v-if="showForm" class="auth-modal">
        <div class="auth-header">
          <h2>Site Access Required</h2>
          <p>Please enter your credentials to access this site.</p>
        </div>

        <form @submit.prevent="handleLogin" class="auth-form">
          <div class="form-group">
            <label for="username">Username:</label>
            <input
              id="username"
              v-model="loginForm.username"
              autocomplete="username"
              type="text"
              required
              :disabled="isLoading"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="password">Password:</label>
            <input
              id="password"
              v-model="loginForm.password"
              type="password"
              autocomplete="current-password"
              required
              :disabled="isLoading"
              class="form-input"
            />
          </div>

          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <button
            type="submit"
            :disabled="isLoading"
            class="login-button"
          >
            {{ isLoading ? 'Logging in...' : 'Login' }}
          </button>
        </form>

        <div class="auth-footer">
          <small>Access is restricted to authorized users only.</small>
        </div>
      </div>
      <div class="auth-show_form" @click="showForm = !showForm"><icon-account-details class="show_form-icon"/></div>
    </div>
  </div>
</template>

<script>
import { useAuthStore, useRoute } from '#imports'
import IconAccountDetails from "~/assets/icons/IconAccountDetails.svg";

export default {
  name: 'SiteAuth',

  setup() {
    return {
      route: useRoute()
    }
  },
  components: {
    IconAccountDetails,
  },
  data() {
    return {
      loginForm: {
        username: '',
        password: ''
      },
      isLoading: false,
      showForm: false,
      errorMessage: ''
    }
  },

  computed: {
    authStore() {
      return useAuthStore()
    },
    routePath() {
      return this.route.path
    }
  },

  methods: {
    async handleLogin() {
      this.isLoading = true
      this.errorMessage = ''

      try {
        const result = await this.authStore.siteLogin(
          this.loginForm.username.trim(),
          this.loginForm.password
        )

        if (!result.success) {
          this.errorMessage = result.message || 'Login failed. Please try again.'
          this.loginForm.password = '' // Clear password on failure
        }
      } catch (error) {
        console.error('Login error:', error)
        this.errorMessage = 'An error occurred during login. Please try again.'
      } finally {
        this.isLoading = false
      }
    }
  },

  watch: {
    routePath() {
      this.authStore.initializeAuth()
    }
  }
}
</script>

<style lang="scss">
@use "~/common/scss/helpers";

.auth-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: url(https://int.test-env.asoundeffect.com/wp-content/maintenance/assets/images/bg.jpg);
  background-size: cover;
  height: 100%;
  min-height: 100%;
  z-index: 999;
}

.auth-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  overflow: hidden;

  .auth-show_form {
    position: fixed;
    bottom: 0;
    right: 8px;
    cursor: pointer;
  }

  .show_form-icon {
    flex: 0 0 helpers.px-to-rem(24);
    height: helpers.px-to-rem(24);
    width: helpers.px-to-rem(24);
  }

  .auth-modal {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
    width: 100%;
    max-width: 400px;
    margin: 1rem;
  }
  
  .auth-header {
    text-align: center;
    margin-bottom: 2rem;
  }
  
  .auth-header h2 {
    color: #333;
    margin-bottom: 0.5rem;
  }

  .auth-header--maintenance {
    max-width: 640px;
    h2 {
      color: #7d7d7d;
      font-size: 24px;
    }
  }
  
  .auth-header p {
    color: #666;
    font-size: 0.9rem;
  }

  .auth-header a {
    color: #dcdcdc;
  }
  
  .auth-form {
    margin-bottom: 1.5rem;
  }

  .auth_social-links {
    display: flex;
    justify-content: center;
    gap: 3rem;
    margin-top: 9rem;
  }
  
  .form-group {
    margin-bottom: 1rem;
  }
  
  .form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
  }
  
  .form-input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    transition: border-color 0.3s ease;
  }
  
  .form-input:focus {
    outline: none;
    border-color: #007cba;
    box-shadow: 0 0 0 2px rgba(0, 124, 186, 0.2);
  }
  
  .form-input:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
  
  .error-message {
    background: #fee;
    color: #c33;
    padding: 0.75rem;
    border-radius: 4px;
    margin-bottom: 1rem;
    font-size: 0.9rem;
  }
  
  .login-button {
    width: 100%;
    padding: 0.75rem;
    background: #007cba;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 1rem;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  
  .login-button:hover:not(:disabled) {
    background: #005a87;
  }
  
  .login-button:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  .auth-footer {
    text-align: center;
    color: #666;
  }
}
</style>
